import { useMemo } from 'react'
import type { TikTokAdSignalRow } from '../../hooks/useCompetitiveData'

interface TikTokAdTrackerProps {
  adSignals: TikTokAdSignalRow[]
  title?: string
  emptyMessage?: string
}

function spendTone(spendTier: string | null): { cardBg: string; cardBorder: string; badgeBg: string; badgeColor: string } {
  const normalized = (spendTier ?? '').toLowerCase()

  if (normalized === 'high') {
    return {
      cardBg: 'var(--danger-50)',
      cardBorder: 'var(--danger-100)',
      badgeBg: 'var(--danger-100)',
      badgeColor: 'var(--danger-700)',
    }
  }

  if (normalized === 'medium') {
    return {
      cardBg: 'var(--warning-50)',
      cardBorder: 'var(--warning-100)',
      badgeBg: 'var(--warning-100)',
      badgeColor: 'var(--warning-700)',
    }
  }

  return {
    cardBg: 'var(--gray-50)',
    cardBorder: 'var(--gray-200)',
    badgeBg: 'var(--gray-200)',
    badgeColor: 'var(--gray-700)',
  }
}

function formatCount(value: number): string {
  return Math.round(value || 0).toLocaleString('en-US')
}

function formatAdType(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

export default function TikTokAdTracker({
  adSignals,
  title = 'Recent Paid TikTok Content',
  emptyMessage = 'No recent paid TikTok content detected.',
}: TikTokAdTrackerProps) {
  const grouped = useMemo(() => {
    const map = new Map<string, TikTokAdSignalRow[]>()

    for (const signal of adSignals) {
      const key = signal.competitor_name || 'Unknown competitor'
      if (!map.has(key)) {
        map.set(key, [])
      }
      map.get(key)?.push(signal)
    }

    return Array.from(map.entries()).map(([competitorName, signals]) => ({
      competitorName,
      signals: signals.sort((a, b) => new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime()),
    }))
  }, [adSignals])

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">{title}</h3>

      {grouped.length === 0 ? (
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">{emptyMessage}</div>
      ) : (
        <div className="mt-3 space-y-4">
          {grouped.map((group) => (
            <div key={group.competitorName} className="space-y-2">
              <p className="text-sm font-semibold text-slate-900">{group.competitorName}</p>

              <div className="space-y-2">
                {group.signals.map((signal) => {
                  const tone = spendTone(signal.spend_tier)

                  return (
                    <article
                      key={`${signal.video_id}-${signal.id}`}
                      className="rounded-lg border p-3"
                      style={{
                        backgroundColor: tone.cardBg,
                        borderColor: tone.cardBorder,
                      }}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs font-semibold text-slate-700">
                          {formatAdType(signal.ad_type)}
                        </span>
                        <span
                          className="rounded-full px-2 py-0.5 text-xs font-semibold"
                          style={{
                            backgroundColor: tone.badgeBg,
                            color: tone.badgeColor,
                          }}
                        >
                          {(signal.spend_tier ?? 'low').toUpperCase()} spend
                        </span>
                        {signal.has_tiktok_shop && (
                          <span className="rounded-full border border-[var(--brand-200)] bg-[var(--brand-50)] px-2 py-0.5 text-xs font-semibold text-[var(--brand-700)]">
                            TikTok Shop
                          </span>
                        )}
                      </div>

                      <div className="mt-2 grid grid-cols-1 gap-2 text-xs text-slate-600 sm:grid-cols-2">
                        <span>Views: {formatCount(signal.view_count)}</span>
                        <span>Likes: {formatCount(signal.like_count)}</span>
                      </div>
                    </article>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
